import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowRight, CheckSquare, Sparkles, Users, Calendar, BookOpen,
  Flag, MessageCircle, MapPin, Compass, Trophy, TrendingUp, Flame,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import PortalLayout from './PortalLayout';
import SEO from '../../components/SEO';
import {
  Skeleton, SkeletonAvatar, SkeletonDashboardCard,
} from '../../components/Skeleton';
import CommunityMatchCard from '../../components/CommunityMatchCard';
import RecentlyPublishedFeed from '../../components/RecentlyPublishedFeed';
import GuidedUpsell from '../../components/GuidedUpsell';
import { trackBlueprintUnlocked, trackPurchase, trackSignUp } from '../../lib/analytics';
import { notifySuccess, notifyError } from '../../lib/notify';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const authHeaders = () => {
  const t = localStorage.getItem('auth_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const relativeTime = (iso) => {
  if (!iso) return '';
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
};

// ============== MOVER OF THE WEEK ==============
const MoverOfTheWeek = () => {
  const [mover, setMover] = useState(undefined); // undefined = loading, null = none
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/community/leaderboard/movers?limit=1`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then(async (r) => (r.ok ? r.json() : null))
      .then((d) => setMover(d?.movers?.[0] || null))
      .catch(() => { setError(true); setMover(null); });
  }, []);

  if (mover === undefined) {
    return (
      <article className="relative overflow-hidden bg-white border border-[#EFE6CF] rounded-2xl p-6 md:p-8">
        <div className="flex items-center gap-5">
          <SkeletonAvatar size={72} />
          <div className="flex-1 space-y-2.5">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-3 w-36" />
          </div>
        </div>
      </article>
    );
  }

  if (error || !mover) {
    // Don't render an empty box - quietly degrade to nothing
    return null;
  }

  const name = `${mover.first_name}${mover.last_name_initial ? ` ${mover.last_name_initial}.` : ''}`;
  const rankChange = mover.rank_change || 0;

  return (
    <article
      className="relative overflow-hidden bg-gradient-to-br from-[#FFF8EA] via-white to-white border border-[#E8A830]/30 rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
      data-testid="mover-of-week-card"
    >
      {/* Golden Hour accent strip */}
      <div
        className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E8A830] via-[#F0B95C] to-[#E8A830]"
        aria-hidden="true"
      />

      {/* Decorative trophy - placed in top-right corner, well clear of any link */}
      <Trophy
        className="absolute top-4 right-6 w-20 h-20 text-[#E8A830]/10 rotate-12"
        aria-hidden="true"
      />

      <div className="relative flex items-start gap-2 mb-4">
        <div className="flex items-center gap-2">
          <span className="font-mono uppercase text-[10px] tracking-[0.18em] text-[#E8A830] font-semibold">
            Mover of the Week
          </span>
          <span className="w-1 h-1 rounded-full bg-[#E8A830]/40" aria-hidden="true" />
          <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">
            7-day climb
          </span>
        </div>
      </div>

      <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
        {/* Avatar with golden ring */}
        <div className="relative">
          <div className="absolute inset-0 -m-1 rounded-full bg-gradient-to-br from-[#E8A830] to-[#F0B95C] blur-sm opacity-70" aria-hidden="true" />
          {mover.profile_photo_url ? (
            <img
              src={mover.profile_photo_url}
              alt=""
              className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover ring-4 ring-white"
            />
          ) : (
            <div
              className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#0A1F2E] text-[#F9F6EF] flex items-center justify-center ring-4 ring-white"
            >
              <span className="font-display italic text-3xl leading-none">
                {(mover.first_name?.[0] || '?').toUpperCase()}
              </span>
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <h3
            className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] leading-tight"
            data-testid="mover-name"
          >
            {name}
          </h3>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {mover.tier_badge && (
              <span
                className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#2CA8A0]/10 text-[#1A5C6E]"
                data-testid="mover-tier-badge"
              >
                {mover.tier_badge}
              </span>
            )}
            {mover.destination_city && (
              <span className="flex items-center gap-1 text-xs text-[#0A1F2E]/65" data-testid="mover-city">
                <MapPin className="w-3 h-3" />
                {mover.destination_city}
              </span>
            )}
            <span className="flex items-center gap-1 text-xs text-[#0A1F2E]/55">
              · Rank #{mover.current_rank}
              {rankChange > 0 && (
                <span className="flex items-center gap-0.5 text-[#2CA8A0] font-medium">
                  <TrendingUp className="w-3 h-3" />+{rankChange}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Points-this-week pill */}
        <div className="flex flex-col items-start sm:items-end shrink-0">
          <span className="font-mono uppercase text-[9px] tracking-[0.15em] text-[#0A1F2E]/55 mb-0.5">
            Points this week
          </span>
          <span
            className="font-display italic text-4xl md:text-5xl text-[#E8A830] leading-none"
            data-testid="mover-points-gained"
          >
            +{mover.points_gained}
          </span>
        </div>
      </div>

      <div className="relative flex items-center justify-between mt-5 pt-5 border-t border-[#E8A830]/15">
        <p className="text-sm text-[#0A1F2E]/70 font-body italic">
          A nudge from the front of the pack - you could be here next week.
        </p>
        <Link
          to="/portal/community/leaderboard"
          className="font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#1A5C6E] hover:text-[#2CA8A0] transition flex items-center gap-1.5"
          data-testid="mover-leaderboard-link"
        >
          See leaderboard <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </article>
  );
};

// ============== BLUEPRINT PENDING PILL (post-onboarding hero) ==============
const BlueprintPendingPill = ({ status }) => {
  // status: 'pending' | 'generating' | 'done' | null
  if (!status || (status !== 'pending' && status !== 'generating' && status !== 'done')) {
    return null;
  }
  const isDone = status === 'done';

  return (
    <div
      className={`mb-6 rounded-full border px-5 py-3 flex items-center justify-between gap-4 transition-all duration-500 ${
        isDone
          ? 'bg-[#2CA8A0]/12 border-[#2CA8A0]/30'
          : 'bg-[#E8A830]/12 border-[#E8A830]/40 animate-pulse'
      }`}
      data-testid="blueprint-pending-pill"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Sparkles
          className={`w-4 h-4 flex-shrink-0 ${isDone ? 'text-[#1A5C6E]' : 'text-[#E8A830]'}`}
          aria-hidden="true"
        />
        <span
          className={`text-sm font-medium truncate ${
            isDone ? 'text-[#1A5C6E]' : 'text-[#8C5B12]'
          }`}
          data-testid="blueprint-pending-pill-label"
        >
          {isDone ? 'Your Blueprint is ready' : 'Your plan is generating…'}
        </span>
      </div>
      <Link
        to="/portal/blueprint"
        className={`font-mono uppercase text-[11px] tracking-[0.12em] font-semibold flex-shrink-0 transition ${
          isDone ? 'text-[#1A5C6E] hover:text-[#0A1F2E]' : 'text-[#8C5B12]/60 cursor-not-allowed pointer-events-none'
        }`}
        data-testid="blueprint-pending-pill-cta"
        aria-disabled={!isDone}
      >
        {isDone ? 'Open it →' : 'Stand by'}
      </Link>
    </div>
  );
};


// iter 157 - One-shot post-intake confirmation banner. Reads + clears
// `?blueprint=submitted` from the URL so a reload doesn't re-trigger it.
const BlueprintSubmittedBanner = () => {
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get('blueprint') === 'submitted') {
        url.searchParams.delete('blueprint');
        window.history.replaceState({}, '', url.toString());
      }
    } catch { /* noop */ }
  }, []);
  return (
    <div
      className="mb-6 p-5 bg-[#F4ECD8] border border-[#2CA8A0]/35 rounded-2xl flex items-start gap-3"
      data-testid="blueprint-submitted-banner"
    >
      <span className="mt-0.5 w-6 h-6 rounded-full bg-[#2CA8A0] text-white flex items-center justify-center flex-none">
        <Sparkles className="w-3.5 h-3.5" />
      </span>
      <div>
        <p className="font-display italic text-xl text-[#0A1F2E] leading-tight">
          Your Blueprint is being prepared.
        </p>
        <p className="text-sm text-[#0A1F2E]/75 mt-1">
          You&rsquo;ll receive an email when it&rsquo;s ready.
        </p>
      </div>
    </div>
  );
};

// ============== WELCOME HEADER ==============
const WelcomeHeader = ({ user }) => {
  const name = user?.first_name || user?.name?.split(' ')[0] || 'friend';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Welcome back' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const streak = user?.login_streak || 0;

  return (
    <header className="mb-8" data-testid="dashboard-welcome">
      <p className="font-mono uppercase text-[11px] tracking-[0.15em] text-[#1A5C6E] mb-2">
        Mundalo Member Portal
      </p>
      <div className="flex flex-wrap items-baseline gap-3 md:gap-5">
        <h1 className="font-display italic text-4xl sm:text-5xl lg:text-6xl text-[#0A1F2E] leading-[1.05]">
          {greeting}, {name}.
        </h1>
        {streak > 1 && (
          <StreakBadge streak={streak} />
        )}
      </div>
      <p className="font-body text-[#0A1F2E]/65 mt-3 max-w-xl">
        Where your move is, who's online, and what's worth your next ten minutes.
      </p>
    </header>
  );
};

// ============== STREAK BADGE ==============
const StreakBadge = ({ streak }) => {
  // Tier colors: warm-up (2-6) → on-fire (7-29) → blazing (30+)
  const tier =
    streak >= 30 ? { from: '#E11D48', to: '#E8A830', label: 'BLAZING' } :
    streak >= 7  ? { from: '#E8A830', to: '#F0B95C', label: 'ON FIRE' } :
                   { from: '#F0B95C', to: '#FFE0A8', label: 'STREAK' };

  return (
    <div
      className="group relative inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#0A1F2E] shadow-sm"
      style={{ background: `linear-gradient(135deg, ${tier.from} 0%, ${tier.to} 100%)` }}
      title={`You've logged in ${streak} days in a row. Keep it up!`}
      data-testid="welcome-streak-badge"
    >
      <Flame className="w-3.5 h-3.5" />
      <span data-testid="welcome-streak-count">{streak}</span>
      <span className="text-[10px] opacity-80">DAY · {tier.label}</span>
    </div>
  );
};

// ============== BLUEPRINT STATUS CARD ==============
const BlueprintStatusCard = ({ status, isTrial }) => {
  // If user is on free_trial, Blueprint is locked. Show an upgrade card instead.
  if (isTrial) {
    return (
      <article
        className="bg-white border border-[#EFE6CF] rounded-2xl p-6 transition-shadow hover:shadow-sm"
        data-testid="card-blueprint-status"
      >
        <div className="flex items-start justify-between mb-3">
          <Sparkles className="w-6 h-6 text-[#2CA8A0]" />
          <span className="font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1 rounded-full bg-[#F4ECD8] text-[#1A5C6E]">
            Locked
          </span>
        </div>
        <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-2">Blueprint AI</h3>
        <p className="text-[#0A1F2E]/65 font-body mb-5">
          A 40-page personalized roadmap. Unlock with Blueprint Only ($199) or any higher plan.
        </p>
        <Link
          to="/portal/upgrade?reason=blueprint"
          className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#E8A830] hover:brightness-110 transition"
          data-testid="card-blueprint-cta"
        >
          Unlock Blueprint <ArrowRight className="w-4 h-4" />
        </Link>
      </article>
    );
  }

  // status: { state: 'pending' | 'generating' | 'done' | 'none', updated_at, blueprint_id }
  const STATES = {
    none: {
      title: 'Your Blueprint awaits',
      body: 'A 40-page personalized relocation roadmap. Built around your answers in 8 minutes.',
      cta: { to: '/portal/blueprint', label: 'Start Blueprint' },
      tag: 'Get started',
    },
    pending: {
      title: 'Blueprint queued',
      body: 'Your Blueprint will start generating in a moment.',
      cta: { to: '/portal/blueprint', label: 'View Blueprint' },
      tag: 'Queued',
    },
    generating: {
      title: 'Blueprint generating',
      body: 'Our AI is drafting your custom plan - this usually takes 1–2 minutes.',
      cta: { to: '/portal/blueprint', label: 'Open Blueprint' },
      tag: 'In progress',
    },
    done: {
      title: 'Blueprint ready',
      body: 'Your Mundalo Relocation Blueprint is finalized and ready to download.',
      cta: { to: '/portal/blueprint', label: 'Open Blueprint' },
      tag: 'Ready',
    },
    // iter 87: explicit error state. When Claude generation fails (network
    // hiccup, rate-limit, etc.) the blueprint row's status becomes "error"
    // and the API surfaces state="error" here. Before this entry was added,
    // STATES["error"] was undefined and reading `s.tag` crashed the entire
    // dashboard with "undefined is not an object (evaluating 'a.tag')" -
    // a hard blank-page lockout for the affected user.
    error: {
      title: 'Blueprint needs a retry',
      body: 'Our AI hit a snag. Your purchase is safe - write to hello@mundalo.com and we\'ll regenerate it within minutes.',
      cta: { to: '/portal/blueprint', label: 'Open Blueprint' },
      tag: 'Needs attention',
    },
  };
  // Defensive lookup - any future server-side state value that we forgot
  // to add to this map falls back to the safe "none" entry instead of
  // crashing render. The previous code (`STATES[status?.state || 'none']`)
  // would happily return undefined if state was a string we didn't know.
  const s = STATES[status?.state] || STATES.none;
  const tone =
    status?.state === 'done'
      ? 'bg-[#2CA8A0]/12 text-[#1A5C6E]'
      : status?.state === 'error'
      ? 'bg-[#B91C1C]/10 text-[#B91C1C]'
      : status?.state === 'generating' || status?.state === 'pending'
      ? 'bg-[#E8A830]/15 text-[#8C5B12]'
      : 'bg-[#F4ECD8] text-[#0A1F2E]/70';

  return (
    <article
      className="bg-white border border-[#EFE6CF] rounded-2xl p-6 transition-shadow hover:shadow-sm"
      data-testid="card-blueprint-status"
    >
      <div className="flex items-start justify-between mb-3">
        <Sparkles className="w-6 h-6 text-[#2CA8A0]" />
        <span className={`font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1 rounded-full ${tone}`}>
          {s.tag}
        </span>
      </div>
      <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-2">{s.title}</h3>
      <p className="text-[#0A1F2E]/65 font-body mb-5">{s.body}</p>
      <Link
        to={s.cta.to}
        className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#2CA8A0] hover:text-[#1A5C6E] transition"
        data-testid="card-blueprint-cta"
      >
        {s.cta.label} <ArrowRight className="w-4 h-4" />
      </Link>
    </article>
  );
};

// ============== CHECKLIST PROGRESS CARD ==============
const ChecklistProgressCard = ({ metrics, isTrial }) => {
  // If user is on free_trial, Checklist is locked. Show an upgrade card instead.
  if (isTrial) {
    return (
      <article
        className="bg-white border border-[#EFE6CF] rounded-2xl p-6 transition-shadow hover:shadow-sm"
        data-testid="card-checklist-progress"
      >
        <div className="flex items-start justify-between mb-3">
          <CheckSquare className="w-6 h-6 text-[#1A5C6E]" />
          <span className="font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1 rounded-full bg-[#F4ECD8] text-[#1A5C6E]">
            Locked
          </span>
        </div>
        <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-2">Living Checklist</h3>
        <p className="text-[#0A1F2E]/65 font-body text-sm mb-5">
          47 steps from "I'm thinking about it" to "I live here." Unlocks with any paid plan.
        </p>
        <Link
          to="/portal/upgrade?reason=checklist"
          className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#E8A830] hover:brightness-110 transition"
          data-testid="card-checklist-cta"
        >
          Unlock Checklist <ArrowRight className="w-4 h-4" />
        </Link>
      </article>
    );
  }

  const pct = metrics?.progress_percent ?? 0;
  const completed = metrics?.completed_items ?? 0;
  const total = metrics?.total_items ?? 0;
  return (
    <article
      className="bg-white border border-[#EFE6CF] rounded-2xl p-6 transition-shadow hover:shadow-sm"
      data-testid="card-checklist-progress"
    >
      <div className="flex items-start justify-between mb-3">
        <CheckSquare className="w-6 h-6 text-[#1A5C6E]" />
        <span className="font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1 rounded-full bg-[#F4ECD8] text-[#0A1F2E]/70">
          {pct}%
        </span>
      </div>
      <h3 className="font-display italic text-2xl text-[#0A1F2E] mb-1">Your Checklist</h3>
      <p className="text-[#0A1F2E]/65 font-body text-sm mb-4">
        {total > 0 ? `${completed} of ${total} items complete` : 'No items yet - your roadmap will populate after Blueprint.'}
      </p>
      <div className="h-2 bg-[#F4ECD8] rounded-full overflow-hidden mb-5" aria-hidden="true">
        <div
          className="h-full bg-[#2CA8A0] transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
          data-testid="checklist-progress-bar"
        />
      </div>
      <Link
        to="/portal/checklist"
        className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold text-[#2CA8A0] hover:text-[#1A5C6E] transition"
        data-testid="card-checklist-cta"
      >
        Open Checklist <ArrowRight className="w-4 h-4" />
      </Link>
    </article>
  );
};

// ============== NEXT ACTION CARD ==============
const NextActionCard = ({ blueprintState, metrics, entitlements, isTrial }) => {
  // Pick the most useful next action based on user state
  let suggestion;
  if (isTrial) {
    // Free trial = community on-ramp. Push them to the people, not the AI.
    suggestion = {
      icon: Users, label: 'Meet the community',
      sub: 'Post your first hello on the Newcomers Board. Real people, not bots, will reply today.',
      to: '/portal/community?board=newcomers',
    };
  } else if (!blueprintState || blueprintState === 'none') {
    suggestion = {
      icon: Sparkles, label: 'Generate your Blueprint',
      sub: 'Answer 5 questions and we build a personalized plan in minutes.',
      to: '/portal/blueprint',
    };
  } else if (blueprintState === 'generating' || blueprintState === 'pending') {
    suggestion = {
      icon: Compass, label: 'Explore the Classroom',
      sub: 'Watch the first module preview while your Blueprint generates.',
      to: '/portal/classroom',
    };
  } else if ((metrics?.progress_percent ?? 0) < 25) {
    suggestion = {
      icon: CheckSquare, label: 'Knock out your first 3 checklist items',
      sub: 'Small wins build momentum. Start with what you can do today.',
      to: '/portal/checklist',
    };
  } else if (!entitlements?.has_community) {
    suggestion = {
      icon: Users, label: 'Meet the community',
      sub: 'Connect with people who already made the move.',
      to: '/portal/community',
    };
  } else {
    suggestion = {
      icon: BookOpen, label: 'Continue the Classroom',
      sub: 'Pick up where you left off in your relocation curriculum.',
      to: '/portal/classroom',
    };
  }
  const Icon = suggestion.icon;
  return (
    <article
      className="bg-[#0A1F2E] text-[#F9F6EF] border border-[#0A1F2E] rounded-2xl p-6 transition-shadow hover:shadow-md"
      data-testid="card-next-action"
    >
      <div className="flex items-center gap-2 mb-3">
        <Flag className="w-4 h-4 text-[#E8A830]" />
        <span className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#E8A830]">
          Recommended next
        </span>
      </div>
      <Icon className="w-8 h-8 text-[#F9F6EF]/80 mb-3" />
      <h3 className="font-display italic text-2xl mb-2">{suggestion.label}</h3>
      <p className="text-[#F9F6EF]/70 font-body text-sm mb-5">{suggestion.sub}</p>
      <Link
        to={suggestion.to}
        className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] font-semibold bg-[#E8A830] hover:brightness-95 text-[#0A1F2E] px-4 py-2 rounded-full transition"
        data-testid="card-next-action-cta"
      >
        {suggestion.label.split(' ')[0]} → <ArrowRight className="w-4 h-4" />
      </Link>
    </article>
  );
};


// ============== OVERVIEW BOX (iter 145 Section 1) ==============
// Two clean clickable lists used on the simplified Portal overview.
const OverviewBox = ({ title, items, testid }) => (
  <section
    data-testid={testid}
    className="bg-white border border-[#EFE6CF] rounded-2xl p-7 md:p-8"
  >
    <h3 className="font-display italic text-3xl text-[#0A1F2E] mb-6">{title}</h3>
    <ul className="space-y-1">
      {items.map(({ to, icon: Icon, label }) => (
        <li key={to}>
          <Link
            to={to}
            data-testid={`${testid}-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
            className="flex items-center gap-4 px-3 py-3 -mx-3 rounded-xl hover:bg-[#F9F6EF] transition group"
          >
            <span className="w-10 h-10 bg-[#F4ECD8] rounded-lg flex items-center justify-center text-[#1A5C6E] group-hover:bg-[#2CA8A0] group-hover:text-white transition">
              <Icon className="w-[18px] h-[18px]" />
            </span>
            <span className="text-[#0A1F2E] font-medium text-[15px]">{label}</span>
            <ArrowRight className="w-4 h-4 ml-auto text-[#0A1F2E]/25 group-hover:text-[#2CA8A0] transition" />
          </Link>
        </li>
      ))}
    </ul>
  </section>
);

// ============== COMMUNITY PULSE (slim widget - 2 most recent posts) ==============
const CommunityPulseWidget = () => {
  const [posts, setPosts] = useState(undefined); // undefined = loading

  useEffect(() => {
    fetch(`${API_URL}/api/community/posts?channel=All`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => setPosts((d.posts || []).slice(0, 2)))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section
      className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-2xl p-6"
      data-testid="community-pulse-widget"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display italic text-2xl text-[#0A1F2E]">
          The conversation today.
        </h3>
        <Link
          to="/portal/community"
          className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0] transition"
          data-testid="community-pulse-all"
        >
          All →
        </Link>
      </div>

      {posts === undefined ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="flex gap-3">
              <SkeletonAvatar size={32} />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-2.5 w-28" />
                <Skeleton className="h-2.5 w-3/4" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <p className="text-sm text-[#0A1F2E]/55 italic">
          No posts yet - be the first to say hi.
        </p>
      ) : (
        <ul className="space-y-3">
          {posts.map((p) => (
            <li
              key={p.post_id}
              className="flex gap-3"
              data-testid={`community-pulse-post-${p.post_id}`}
            >
              <div
                className="rounded-full bg-[#2CA8A0] text-white text-xs font-medium w-8 h-8 flex items-center justify-center shrink-0 overflow-hidden"
                aria-hidden="true"
              >
                {p.author?.profile_photo_url ? (
                  <img src={p.author.profile_photo_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  (p.author?.first_name || '?')[0]?.toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] leading-tight">
                  <span className="font-medium text-[#0A1F2E]">
                    {p.author?.first_name || 'Member'}
                  </span>
                  <span className="text-[#0A1F2E]/50"> · </span>
                  <span className="font-mono uppercase text-[10px] tracking-wider text-[#1A5C6E]">
                    {p.channel}
                  </span>
                  <span className="text-[#0A1F2E]/50"> · {relativeTime(p.created_at)}</span>
                </p>
                <p className="text-sm text-[#0A1F2E]/85 mt-0.5 line-clamp-2">{p.content}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ============== FEATURED PARTNER (slim widget - 1 rotating vetted partner) ==============
const FeaturedPartnerWidget = () => {
  const [partner, setPartner] = useState(undefined); // undefined = loading

  useEffect(() => {
    fetch(`${API_URL}/api/partners/featured`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { partner: null }))
      .then((d) => setPartner(d?.partner || null))
      .catch(() => setPartner(null));
  }, []);

  if (partner === null) return null; // graceful no-op when no vetted partners

  return (
    <section
      className="bg-[#F4ECD8] border border-[#EFE6CF] rounded-2xl p-6"
      data-testid="featured-partner-widget"
    >
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display italic text-2xl text-[#0A1F2E]">
          Vetted for your move.
        </h3>
        <Link
          to="/portal/partners"
          className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] hover:text-[#2CA8A0] transition"
          data-testid="featured-partner-all"
        >
          All partners →
        </Link>
      </div>

      {partner === undefined ? (
        <div className="flex gap-4">
          <Skeleton className="w-12 h-12 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-2.5 w-3/4" />
            <Skeleton className="h-2.5 w-1/2" />
          </div>
        </div>
      ) : (
        <Link
          to={`/portal/partners/${partner.id}`}
          className="flex gap-4 -m-2 p-2 rounded-xl hover:bg-white/60 transition group"
          data-testid={`featured-partner-${partner.id}`}
        >
          <div className="w-12 h-12 rounded-lg bg-white border border-[#EFE6CF] flex items-center justify-center shrink-0 overflow-hidden">
            {partner.logo_url ? (
              <img src={partner.logo_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display italic text-xl text-[#1A5C6E]">
                {(partner.name?.[0] || '?').toUpperCase()}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E] mb-0.5">
              {partner.category || 'Vetted Partner'}
            </p>
            <p className="font-medium text-[15px] text-[#0A1F2E] truncate">
              {partner.name}
            </p>
            <p className="text-sm text-[#0A1F2E]/65 mt-1 line-clamp-2">
              {partner.description || 'Member-vetted, hand-picked for your move.'}
            </p>
          </div>
          <ArrowRight className="w-4 h-4 mt-1 text-[#0A1F2E]/30 group-hover:text-[#2CA8A0] transition shrink-0" />
        </Link>
      )}
    </section>
  );
};

// ============== COMMUNITY PREVIEW ==============
const CommunityPreview = () => {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/community/posts?channel=All`, {
      credentials: 'include',
      headers: authHeaders(),
    })
      .then((r) => (r.ok ? r.json() : { posts: [] }))
      .then((d) => setPosts((d.posts || []).slice(0, 3)))
      .catch(() => setPosts([]));
  }, []);

  return (
    <section
      className="bg-white border border-[#EFE6CF] rounded-2xl p-6"
      data-testid="community-preview"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#2CA8A0]" />
          <h3 className="font-display italic text-2xl text-[#0A1F2E]">From the community</h3>
        </div>
        <Link
          to="/portal/community"
          className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#2CA8A0] hover:text-[#1A5C6E]"
          data-testid="community-preview-all"
        >
          See all →
        </Link>
      </div>

      {posts === null ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-3">
              <SkeletonAvatar size={36} />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-2.5 w-3/4" />
                <Skeleton className="h-2.5 w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-[#0A1F2E]/55 italic mb-3">No community posts yet.</p>
          <Link
            to="/portal/community"
            className="inline-flex items-center gap-2 font-mono uppercase text-[11px] tracking-[0.12em] text-[#2CA8A0] hover:text-[#1A5C6E]"
          >
            Start the first conversation →
          </Link>
        </div>
      ) : (
        <ul className="space-y-4">
          {posts.map((p) => (
            <li key={p.post_id} className="flex gap-3" data-testid={`community-preview-${p.post_id}`}>
              <div
                className="rounded-full bg-[#2CA8A0] text-white text-sm font-medium w-9 h-9 flex items-center justify-center shrink-0"
                aria-hidden="true"
              >
                {p.author?.profile_photo_url ? (
                  <img src={p.author.profile_photo_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  (p.author?.first_name || '?')[0]?.toUpperCase()
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-medium text-[#0A1F2E]">
                    {p.author?.first_name || 'Member'}
                  </span>
                  <span className="text-[#0A1F2E]/55"> · </span>
                  <span className="font-mono uppercase text-[10px] tracking-wider text-[#2CA8A0]">
                    {p.channel}
                  </span>
                  <span className="text-[#0A1F2E]/55"> · {relativeTime(p.created_at)}</span>
                </p>
                <p className="text-sm text-[#0A1F2E]/85 mt-1 line-clamp-2">{p.content}</p>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#0A1F2E]/55">
                  <span className="flex items-center gap-1">
                    <Flag className="w-3 h-3" />{p.reactions?.total ?? 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageCircle className="w-3 h-3" />{p.comment_count ?? 0}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

// ============== MAIN ==============
const PortalDashboard = () => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // ============================================================
  // Post-checkout activation handshake.
  // When Stripe redirects back to `/portal/dashboard?upgraded=true&session_id=...`
  // we deterministically activate the user's tier (no webhook race) and
  // refresh AuthContext so the new role is reflected in the UI immediately.
  // Idempotent: safe to run repeatedly (only fires once per session_id thanks
  // to sessionStorage).
  // ============================================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('upgraded') !== 'true') return;
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const onceKey = `mundalo_activated_${sessionId}`;
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(onceKey)) return;
    if (typeof window !== 'undefined') window.sessionStorage.setItem(onceKey, '1');

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/billing/activate-from-session`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ session_id: sessionId }),
        });
        const data = await r.json().catch(() => ({}));
        if (cancelled) return;

        if (r.ok && data.activated) {
          // Refresh AuthContext so the rest of the UI sees the new role.
          try { await checkAuth?.(); } catch { /* swallow */ }
          const tierLabel = ({
            blueprint_only: 'Blueprint', essentials: 'Essentials', guided: 'Guided',
            community: 'Community', connected: 'Connected',
          })[data.role] || 'your new plan';
          notifySuccess(`You're now on ${tierLabel} - welcome aboard.`);

          // iter 118 - GA4 `purchase` conversion event.
          // Fires exactly once per Stripe session (trackPurchase keeps its
          // own sessionStorage guard keyed on transaction_id). The amounts
          // are pulled from the canonical /constants/tiers.js price map so
          // they stay in lockstep with what's actually charged.
          try {
            const planMap = {
              blueprint_only: { plan_name: 'Blueprint Only', value: 149 },
              essentials:     { plan_name: 'Essentials',     value: 299 },
              guided:         { plan_name: 'Guided',         value: 499 },
              community:      { plan_name: 'Community',      value: 9.99 },
              connected:      { plan_name: 'Connected',      value: 19.99 },
            };
            const meta = planMap[data.role];
            if (meta) {
              trackPurchase({
                plan_name: meta.plan_name,
                value: meta.value,
                currency: 'USD',
                transaction_id: sessionId,
              });
            }
          } catch { /* analytics is fire-and-forget */ }
        } else if (r.ok && !data.activated) {
          // Payment not yet recognized by Stripe (rare); just refresh quietly.
          try { await checkAuth?.(); } catch { /* swallow */ }
        } else {
          notifyError(data, 'We received your payment but could not auto-activate your plan. Refresh in a moment or email hello@mundalo.com.');
        }
      } catch (e) {
        if (!cancelled) console.error('[post-checkout] activation failed:', e);
      } finally {
        // Strip the query params so a refresh doesn't replay the handshake.
        if (!cancelled) {
          navigate('/portal/dashboard', { replace: true });
        }
      }
    })();

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // ============================================================
  // iter 118 - GA4 `sign_up` conversion event.
  // Fires the FIRST time a newly-created free-trial user lands on the
  // dashboard. Detected via the `?welcome=trial` param that
  // pages/StartFreeTrial.js attaches to its redirect. Guarded by
  // sessionStorage so a refresh doesn't double-count.
  // ============================================================
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('welcome') !== 'trial') return;
    try {
      const onceKey = 'ga4_signup_fired';
      if (typeof window !== 'undefined' && window.sessionStorage) {
        if (window.sessionStorage.getItem(onceKey)) return;
        window.sessionStorage.setItem(onceKey, '1');
      }
      trackSignUp({ method: 'email' });
    } catch { /* analytics is fire-and-forget */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [entitlements, setEntitlements] = useState(null);
  const [portalData, setPortalData] = useState(null);
  const [blueprintState, setBlueprintState] = useState(null);
  const [pendingBlueprint, setPendingBlueprint] = useState({
    id: user?.latest_blueprint_id || null,
    status: user?.latest_blueprint_status || null,
  });
  const [dataLoading, setDataLoading] = useState(true);

  // Keep pending-pill state in sync with the user record after auth refresh.
  useEffect(() => {
    if (user?.latest_blueprint_id) {
      setPendingBlueprint((p) => ({
        id: user.latest_blueprint_id,
        status: user.latest_blueprint_status || p.status || 'pending',
      }));
    }
  }, [user?.latest_blueprint_id, user?.latest_blueprint_status]);

  // GA4 - fire blueprint_unlocked once when the user's role flips to a tier
  // that grants Blueprint. SessionStorage key prevents re-firing on refresh.
  useEffect(() => {
    if (!user?.role || !user?.email) return;
    if (!['essentials', 'guided', 'community', 'connected'].includes(user.role)) return;
    const key = `mundalo_bp_unlocked_${user.email}_${user.role}`;
    if (typeof window === 'undefined') return;
    if (window.sessionStorage.getItem(key)) return;
    trackBlueprintUnlocked({ tier: user.role });
    window.sessionStorage.setItem(key, '1');
  }, [user?.role, user?.email]);

  // Poll the blueprint status every 5 s until it's done.
  useEffect(() => {
    const id = pendingBlueprint.id;
    if (!id) return;
    if (pendingBlueprint.status === 'done') return;
    let cancelled = false;

    const tick = async () => {
      try {
        const r = await fetch(
          `${API_URL}/api/blueprint/status/${id}`,
          { credentials: 'include', headers: { ...authHeaders() } },
        );
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        const s = (d?.status || '').toLowerCase();
        if (s) {
          setPendingBlueprint((p) => ({ ...p, status: s }));
          if (s === 'done' || s === 'complete' || s === 'ready') {
            setBlueprintState('done');
            // Re-fetch /auth/me so AuthContext mirrors the new status.
            try { await checkAuth?.(); } catch { /* swallow */ }
          }
        }
      } catch { /* swallow */ }
    };
    tick(); // immediate
    const handle = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(handle); };
  }, [pendingBlueprint.id, pendingBlueprint.status]);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    setDataLoading(true);
    const headers = { ...authHeaders() };
    const [pdRes, entRes, bpRes] = await Promise.all([
      fetch(`${API_URL}/api/portal/me`, { credentials: 'include', headers }).catch(() => null),
      fetch(`${API_URL}/api/portal/entitlements`, { credentials: 'include', headers }).catch(() => null),
      fetch(`${API_URL}/api/blueprint/by-email/${encodeURIComponent(user.email)}`, { credentials: 'include', headers }).catch(() => null),
    ]);
    if (pdRes?.ok) setPortalData(await pdRes.json());
    else setPortalData({ profile: {}, metrics: {}, outstanding_items: [] });
    if (entRes?.ok) setEntitlements(await entRes.json());
    if (bpRes?.ok) {
      const d = await bpRes.json();
      setBlueprintState(d.status || 'done');
    } else {
      setBlueprintState('none');
    }
    setDataLoading(false);
  }, [user]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F6EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#2CA8A0] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/portal" replace />;

  const metrics = portalData?.metrics || {};
  const cityHint = portalData?.profile?.destination_city || entitlements?.destination_city;
  const isTrial = user?.role === 'free_trial';

  return (
    <PortalLayout activeTab="overview">
      <SEO
        title="Dashboard"
        description="Your Mundalo member dashboard - Blueprint, Checklist, and the community at a glance."
        path="/portal/dashboard"
        noindex
      />

      <div
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12"
        style={{ animation: 'fadeIn 0.35s ease-out' }}
      >
        <WelcomeHeader user={user} />

        {/* iter 157 - One-shot confirmation banner after a paid user
            submits the Blueprint intake. We clear the URL param so a
            page reload doesn't re-show it. */}
        {new URLSearchParams(location.search).get('blueprint') === 'submitted' && (
          <BlueprintSubmittedBanner />
        )}

        {cityHint && (
          <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 bg-[#F4ECD8] rounded-full font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E]">
            <MapPin className="w-3.5 h-3.5" />
            Moving to {cityHint}
          </div>
        )}

        {/* iter 145 (Section 1) - Simplified overview: two clean boxes.
            Box 1: Your Guide to Moving · Box 2: Community Life.
            Plus a single subtle link to the Community Boards. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-testid="dashboard-overview-boxes">
          <OverviewBox
            title="Your Guide to Moving"
            testid="overview-box-move"
            items={[
              { to: '/portal/blueprint',    icon: Sparkles,    label: 'Blueprint AI' },
              { to: '/portal/checklist',    icon: CheckSquare, label: 'Checklist' },
              { to: '/portal/classroom',    icon: BookOpen,    label: 'Classroom' },
              { to: '/portal/city-guides',  icon: MapPin,      label: 'City Guides' },
              { to: '/portal/schedule-call',icon: Calendar,    label: 'Schedule a Call' },
            ]}
          />
          <OverviewBox
            title="Community Life"
            testid="overview-box-life"
            items={[
              { to: '/portal/events',      icon: Calendar,     label: 'Events' },
              { to: '/portal/adventures',  icon: Compass,      label: 'Adventures' },
              { to: '/portal/local-deals', icon: Flag,         label: 'Local Deals' },
              { to: '/portal/housing',     icon: MapPin,       label: 'Housing' },
              { to: '/portal/partners',    icon: Users,        label: 'Vetted Partners' },
            ]}
          />
        </div>

        {/* iter 158 - Slim widgets below the two main boxes:
            "Community Pulse" (2 most recent posts) + "Featured Partner"
            (1 daily-rotating vetted partner). Adds a heartbeat to the
            otherwise-static overview without adding clutter. */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8"
          data-testid="dashboard-pulse-row"
        >
          <CommunityPulseWidget />
          <FeaturedPartnerWidget />
        </div>

        <div className="text-center">
          <Link
            to="/portal/community"
            data-testid="overview-community-boards-link"
            className="inline-flex items-center gap-1.5 text-sm text-[#0A1F2E]/55 hover:text-[#1A5C6E] transition-colors"
          >
            Go to Community Boards
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </PortalLayout>
  );
};

export default PortalDashboard;
